import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useState } from 'react';

import useService from '@/lib/hooks/useService';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';
import type { Uploads } from '@/types/database/uploads';
import type UserGroupRoute from '@/types/database/userGroupRoute';

import AddAndEditPlanModalWithButton from '../AddOrEditModalWithButton';
import {
  checkDetailModalBodyStyles,
  getAllPlanOptions,
  getServiceOptionsForFilter,
  getSubscriptionPlansColumns,
  renderPlanDetail,
  serviceActiveOptions,
} from '../helper';
import type { ServiceData } from '../types';
import styles from './index.module.scss';

const { confirm } = Modal;
const { Title, Text } = Typography;
const { Item, useForm } = Form;

const SubscriptionPlanSetting = () => {
  const [subscriptionPlans, updateSubscriptionPlans, subscriptionPlanService] =
    useService<SubscriptionPlan>({
      collectionName: 'subscriptionPlan',
    });
  const [uploads, updateUploads] = useService<Uploads>({
    collectionName: 'uploads',
  });
  const [userGroupRoutes, updateUserGroupRoute, userGroupRouteService] =
    useService<UserGroupRoute>({
      collectionName: 'userGroupRoute',
    });

  const [editServiceForm] = useForm();

  const [isOpenCheckDetailModal, setIsOpenCheckDetailModal] = useState(false);
  const [isOpenEditServiceModal, setIsOpenEditServiceModal] = useState(false);
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [selectedServiceName, setSelectedServiceName] = useState('');
  const [subscriptionPlanDetailRecord, setSubscriptionPlanDetailRecord] =
    useState<SubscriptionPlan>();
  const [editingServiceRecord, setEditingServiceRecord] =
    useState<ServiceData>();

  const checkDetail = (record: SubscriptionPlan) => {
    setSubscriptionPlanDetailRecord(record);
    setIsOpenCheckDetailModal(true);
  };

  const updatePlanAvailability = useCallback(
    async (record: SubscriptionPlan) => {
      try {
        await subscriptionPlanService.patch(record._id, {
          isPlanActive: !record.isPlanActive,
        });
        updateSubscriptionPlans();
        message.success('方案變更成功!');
      } catch (error) {
        message.error('方案變更失敗!');
      }
    },
    [subscriptionPlanService, updateSubscriptionPlans]
  );

  const showUpdatePlanStatusAlert = useCallback(
    (record: SubscriptionPlan) => {
      confirm({
        title: `確定${record.isPlanActive ? '停用' : '啟用'}該方案嗎？`,
        icon: <ExclamationCircleOutlined />,
        content: `若確定${record.isPlanActive ? '停用' : '啟用'}請按確認鍵`,
        okText: '確認',
        cancelText: '取消',
        onOk: async () => await updatePlanAvailability(record),
      });
    },
    [updatePlanAvailability]
  );

  const allPlanOptions = getAllPlanOptions(subscriptionPlans);

  const subscriptionPlansColumns = getSubscriptionPlansColumns({
    checkDetail,
    updatePlanAvailability: showUpdatePlanStatusAlert,
    updateSubscriptionPlans,
    updateUploads,
    uploads,
    userGroupRoutes,
  });

  const getSubscriptionPlansDataSource = () => {
    let dataSource: (SubscriptionPlan & { serviceName: string })[] = [];

    dataSource = subscriptionPlans.map((plan) => {
      return {
        ...plan,
        serviceName: userGroupRoutes.find(
          (userGroupRoute) => userGroupRoute._id === plan.serviceId
        )?.name as string,
      };
    });

    if (selectedPlanName) {
      dataSource = dataSource.filter(
        (data) => data.planName === selectedPlanName
      );
    }

    if (selectedServiceName) {
      dataSource = dataSource.filter(
        (data) => data.serviceName === selectedServiceName
      );
    }

    return dataSource;
  };

  const subscriptionPlansDataSource = getSubscriptionPlansDataSource();

  const handleEditServiceButtonClick = (record: ServiceData) => {
    setEditingServiceRecord(record);
    editServiceForm.setFieldsValue(record);
    setIsOpenEditServiceModal(true);
  };

  const serviceManagementColumns: ColumnsType<ServiceData> = [
    {
      title: '服務',
      dataIndex: 'serviceName',
    },
    {
      title: '方案功能開/關',
      dataIndex: 'isServiceActive',
      render: (_value, record) => (record.isServiceActive ? '開' : '關'),
    },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 150,
      fixed: 'right',
      render: (_value, record) => (
        <Button onClick={() => handleEditServiceButtonClick(record)}>
          編輯
        </Button>
      ),
    },
  ];

  const getServiceManagementDataSource = () => {
    const dataSource = userGroupRoutes.map((userGroupRoute) => {
      const { _id, name, isServiceActive } = userGroupRoute;
      return {
        _id,
        serviceName: name,
        isServiceActive,
      };
    });

    return dataSource;
  };

  const serviceManagementDataSource = getServiceManagementDataSource();

  const handleEditServiceFormSubmit = async (formValues: ServiceData) => {
    const foundServices = userGroupRoutes.find(
      (userGroupRoute) =>
        userGroupRoute.name === editingServiceRecord?.serviceName
    );

    try {
      await userGroupRouteService.patch(
        null,
        {
          isServiceActive: formValues.isServiceActive,
        },
        {
          query: {
            _id: foundServices?._id,
          },
        }
      );
      updateUserGroupRoute();
      setIsOpenEditServiceModal(false);
      message.success('編輯服務成功!');
    } catch (error) {
      message.error('編輯服務失敗!');
    }
  };

  const serviceOptions = getServiceOptionsForFilter(
    subscriptionPlans,
    userGroupRoutes
  );

  return (
    <Space direction="vertical" className={styles.widthFull} size={50}>
      <Card>
        <Title level={5}>方案功能管理</Title>
        <Table
          bordered
          rowKey="_id"
          columns={serviceManagementColumns}
          dataSource={serviceManagementDataSource}
          pagination={{ defaultPageSize: 5 }}
        />
      </Card>
      <Modal
        open={isOpenEditServiceModal}
        centered
        destroyOnClose
        title="編輯服務"
        onCancel={() => setIsOpenEditServiceModal(false)}
        footer={null}
      >
        <Form
          form={editServiceForm}
          layout="vertical"
          preserve={false}
          initialValues={editingServiceRecord}
          onFinish={handleEditServiceFormSubmit}
        >
          <Item label={<Text type="secondary">服務</Text>}>
            <span>{editingServiceRecord?.serviceName}</span>
          </Item>
          <Item
            label={<Text type="secondary">方案功能開/關</Text>}
            name="isServiceActive"
            rules={[{ required: true, message: '請選擇開關' }]}
          >
            <Select placeholder="請選擇開關" options={serviceActiveOptions} />
          </Item>
          <Item>
            <Space className={styles.footerButtonContainer}>
              <Button onClick={() => setIsOpenEditServiceModal(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Modal>
      <Space direction="vertical" className={styles.widthFull}>
        <AddAndEditPlanModalWithButton
          type="add"
          updateSubscriptionPlans={updateSubscriptionPlans}
          updateUploads={updateUploads}
          userGroupRoutes={userGroupRoutes}
        />
        <Space size="large">
          <span>
            方案：
            <Select
              placeholder="請選擇方案名稱"
              options={allPlanOptions}
              allowClear
              className={styles.select}
              onChange={(planName) => setSelectedPlanName(planName)}
            />
          </span>
          <span>
            服務：
            <Select
              placeholder="請選擇服務"
              options={serviceOptions}
              allowClear
              className={styles.select}
              onChange={(serviceName) => setSelectedServiceName(serviceName)}
            />
          </span>
        </Space>
        <Card>
          <Title level={5}>計費方案</Title>
          <Table
            bordered
            rowKey="_id"
            columns={subscriptionPlansColumns}
            dataSource={subscriptionPlansDataSource}
            scroll={{ x: 'max-content' }}
          />
        </Card>
        <Modal
          open={isOpenCheckDetailModal}
          centered
          bodyStyle={checkDetailModalBodyStyles}
          title="計費方案"
          onCancel={() => setIsOpenCheckDetailModal(false)}
          footer={
            <Button
              type="primary"
              onClick={() => setIsOpenCheckDetailModal(false)}
            >
              關閉
            </Button>
          }
        >
          <Space direction="vertical">
            {renderPlanDetail(subscriptionPlanDetailRecord)}
          </Space>
        </Modal>
      </Space>
    </Space>
  );
};

export default SubscriptionPlanSetting;
